import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;

  try {
    if (cardCode.length < 3) {
      return null;
    }

    // Use cards folder with full card code (e.g., "QDD.png", "10DD.png", "6CC.png")
    // Format: rank + suit (DD, SS, CC, HH)
    return `/assets/img/cards/${cardCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to calculate card score (for 32 Cards game)
const calculateCardScore = (cardCode) => {
  if (!cardCode) return 0;

  // Extract rank from card code (e.g., "6SS" -> "6", "JCC" -> "J", "QCC" -> "Q", "10CC" -> "10")
  const rank = cardCode.replace(/[^0-9JQKA]/g, '');

  // Map face cards and ace
  if (rank === 'J' || rank === 'Q' || rank === 'K') return 10;
  if (rank === 'A') return 1;

  // Number cards
  const num = parseInt(rank, 10);
  return isNaN(num) ? 0 : num;
};

// Helper function to parse card string and split into 4 players (Player 8, 9, 10, 11)
const parseCards = (cardString) => {
  if (!cardString) return { player8: [], player9: [], player10: [], player11: [] };

  const cards = cardString.split(',');
  
  // Player 8: first card
  const player8Cards = cards[0] ? [getCardImage(cards[0].trim())] : [];
  // Player 9: second card
  const player9Cards = cards[1] ? [getCardImage(cards[1].trim())] : [];
  // Player 10: third and fourth cards (if available)
  const player10Cards = [];
  if (cards[2]) player10Cards.push(getCardImage(cards[2].trim()));
  if (cards[4]) player10Cards.push(getCardImage(cards[4].trim()));
  // Player 11: fifth and sixth cards (if available)
  const player11Cards = [];
  if (cards[3]) player11Cards.push(getCardImage(cards[3].trim()));
  if (cards[5]) player11Cards.push(getCardImage(cards[5].trim()));

  // Calculate scores (sum for players with multiple cards)
  const player8Score = cards[0] ? calculateCardScore(cards[0].trim()) : 0;
  const player9Score = cards[1] ? calculateCardScore(cards[1].trim()) : 0;
  const player10Score = (cards[2] ? calculateCardScore(cards[2].trim()) : 0) + 
                        (cards[4] ? calculateCardScore(cards[4].trim()) : 0);
  const player11Score = (cards[3] ? calculateCardScore(cards[3].trim()) : 0) + 
                        (cards[5] ? calculateCardScore(cards[5].trim()) : 0);

  return {
    player8: { cards: player8Cards, score: player8Score },
    player9: { cards: player9Cards, score: player9Score },
    player10: { cards: player10Cards, score: player10Score },
    player11: { cards: player11Cards, score: player11Score },
  };
};

// Helper function to map win value to player number
const getPlayerNumber = (win) => {
  const winMap = {
    "1": "8",
    "2": "9",
    "3": "10",
    "4": "11"
  };
  return winMap[win] || win;
};

// Helper function to parse rdesc for Card32B: "Player 9#8 : Odd  |  9 : Odd~10 : Odd  |  11 : Even#Black#8-9#5"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};

  const parts = rdesc.split('#');
  
  const winner = parts[0] || ""; // "Player 9"
  const oddEven = parts[1] || ""; // "8 : Odd  |  9 : Odd~10 : Odd  |  11 : Even"
  const blackRed = parts[2] || ""; // "Black"
  const total = parts[3] || ""; // "8-9"
  const single = parts[4] || ""; // "5"

  return { winner, oddEven, blackRed, total, single };
};

const ResultCard32B = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('card32eu');
        if (response?.success && response?.data?.data?.res) {
          setLastResults(response.data.data.res);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);

    const mid = resultItem?.mid;

    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('card32eu', mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        } else if (response?.success && response?.data?.t1) {
          setDetailResult(response.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  }, [detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result
  const getModalData = useMemo(() => {
    if (!selectedResult || !showModal || !detailResult) return null;

    const cards = parseCards(detailResult.card);
    const rdescData = parseRdesc(detailResult.rdesc);
    const winner = detailResult.winnat || rdescData.winner || `Player ${getPlayerNumber(detailResult.win)}`;
    const winValue = detailResult.win;

    // Determine which player is the winner
    const isPlayer8Winner = winValue === "1";
    const isPlayer9Winner = winValue === "2";
    const isPlayer10Winner = winValue === "3";
    const isPlayer11Winner = winValue === "4";

    return {
      roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      players: {
        player8: {
          cards: cards.player8.cards,
          score: cards.player8.score,
          isWinner: isPlayer8Winner,
        },
        player9: {
          cards: cards.player9.cards,
          score: cards.player9.score,
          isWinner: isPlayer9Winner,
        },
        player10: {
          cards: cards.player10.cards,
          score: cards.player10.score,
          isWinner: isPlayer10Winner,
        },
        player11: {
          cards: cards.player11.cards,
          score: cards.player11.score,
          isWinner: isPlayer11Winner,
        },
      },
      resultDetails: {
        winner: winner,
        oddEven: rdescData.oddEven || "",
        blackRed: rdescData.blackRed || "",
        total: rdescData.total || "",
        single: rdescData.single || "",
      },
    };
  }, [selectedResult, showModal, detailResult, gameData]);

  const modalData = getModalData;

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : data.map((res, idx) => ({ win: res, mid: null }));

  return (
    <>
      <div className="result-row card32 casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const playerNumber = getPlayerNumber(win);
          return (
            <div
              key={index}
              className={`result-circle result-player-${playerNumber}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {playerNumber}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="32 Cards B Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>

            {/* All Players Section */}
            <div className="row player-container m-t-10">
              <div className="col-md-12">
                <div className="player-image-container row">
                  {/* Player 8 */}
                  <div className="col-md-12 text-center">
                    <h4>
                      Player 8 - <span>{modalData.players.player8.score || 0}</span>
                    </h4>
                    <div className="d-inline-block">
                      {modalData.players.player8.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player 8 Card ${index + 1}`}
                            className="d-inline-block mr-1"
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div>
                      {modalData.players.player8.isWinner ? (
                        <label className="winner-label bg-success m-t-5">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-5">&nbsp;</label>
                      )}
                    </div>
                  </div>

                  {/* Player 9 */}
                  <div className="col-md-12 text-center">
                    <h4>
                      Player 9 - <span>{modalData.players.player9.score || 0}</span>
                    </h4>
                    <div className="d-inline-block">
                      {modalData.players.player9.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player 9 Card ${index + 1}`}
                            className="d-inline-block mr-1"
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div>
                      {modalData.players.player9.isWinner ? (
                        <label className="winner-label bg-success m-t-5">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-5">&nbsp;</label>
                      )}
                    </div>
                  </div>

                  {/* Player 10 */}
                  <div className="col-md-12 text-center">
                    <h4>
                      Player 10 - <span>{modalData.players.player10.score || 0}</span>
                    </h4>
                    <div className="d-inline-block">
                      {modalData.players.player10.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player 10 Card ${index + 1}`}
                            className="d-inline-block mr-1"
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div>
                      {modalData.players.player10.isWinner ? (
                        <label className="winner-label bg-success m-t-5">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-5">&nbsp;</label>
                      )}
                    </div>
                  </div>

                  {/* Player 11 */}
                  <div className="col-md-12 text-center">
                    <h4>
                      Player 11 - <span>{modalData.players.player11.score || 0}</span>
                    </h4>
                    <div className="d-inline-block">
                      {modalData.players.player11.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player 11 Card ${index + 1}`}
                            className="d-inline-block mr-1"
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div>
                      {modalData.players.player11.isWinner ? (
                        <label className="winner-label bg-success m-t-5">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-5">&nbsp;</label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Result Details */}
            {modalData.resultDetails && (
              <div className="row">
                <div className="col-md-12 text-left">
                  {modalData.resultDetails.winner && (
                    <p className="m-b-0 m-t-20">
                      <b>Winner:</b> {modalData.resultDetails.winner}
                    </p>
                  )}
                  {modalData.resultDetails.oddEven && (
                    <p className="m-b-0 m-t-10">
                      <b>Odd/Even:</b> {modalData.resultDetails.oddEven}
                    </p>
                  )}
                  {modalData.resultDetails.blackRed && (
                    <p className="m-b-0 m-t-10">
                      <b>Black/Red:</b> {modalData.resultDetails.blackRed}
                    </p>
                  )}
                  {modalData.resultDetails.total && (
                    <p className="m-b-0 m-t-10">
                      <b>Total:</b> {modalData.resultDetails.total}
                    </p>
                  )}
                  {modalData.resultDetails.single && (
                    <p className="m-b-0 m-t-10">
                      <b>Single:</b> {modalData.resultDetails.single}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultCard32B;

